import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { resolveType } from '../services/ServiceContainer';
import { LeagueService } from '../services/LeagueService';
import { LeagueResultService } from '../services/LeagueResultService';
import { LeagueModel } from '../models/LeagueModels';
import LoadingSpinner from '../components/LoadingSpinner';
import { dayIdToDateString } from '../utils/toolkit';

const LeagueReports: React.FC = () => {
    const { leagueId } = useParams<{ leagueId: string }>();
    const { theme } = useTheme();

    const [loading, setLoading] = useState(true);
    const [leagueModel, setLeagueModel] = useState<LeagueModel | null>(null);
    const [selectedScheduleItems, setSelectedScheduleItems] = useState<Set<string>>(new Set());
    const [generatingPayouts, setGeneratingPayouts] = useState(false);
    const [generatingFlights, setGeneratingFlights] = useState(false);
    const [generatingTees, setGeneratingTees] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            if (!leagueId) {
                setError('Missing league ID');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);

                // Fetch league data
                const leagueService = resolveType(LeagueService);
                const league = await leagueService.get(leagueId);
                setLeagueModel(league);

            } catch (err: any) {
                console.error('Error fetching league data:', err);
                setError(err.message || 'Failed to load league data');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [leagueId]);

    const formatDate = (dayId: string) => {
        const [year, month, day] = dayIdToDateString(dayId).split('-');
        return new Date(parseInt(year), parseInt(month) - 1, parseInt(day)).toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric'
        });
    };

    const getMemberName = (encodedEmail: string): string => {
        if (!leagueModel || !leagueModel.members) return encodedEmail;
        const member = leagueModel.members[encodedEmail];
        return member ? `${member.firstName} ${member.lastName}` : encodedEmail;
    };

    const handleScheduleItemToggle = (dayId: string) => {
        const newSelected = new Set(selectedScheduleItems);
        if (newSelected.has(dayId)) {
            newSelected.delete(dayId);
        } else {
            newSelected.add(dayId);
        }
        setSelectedScheduleItems(newSelected);
    };



    const generatePayoutsCSV = async () => {
        if (!leagueModel || selectedScheduleItems.size === 0) return;

        setGeneratingPayouts(true);
        try {
            const leagueResultService = resolveType(LeagueResultService);

            // Collect all member payout data
            const memberPayouts: Record<string, {
                firstName: string;
                lastName: string;
                totalLeaguePayout: number;
                totalSkinPayout: number;
                totalPayout: number;
                flightName: string;
                payouts: Array<{
                    description: string;
                    amount: number;
                    dayId: string;
                    code?: string;
                    hole?: number;
                }>;
            }> = {};

            // Process each selected schedule item
            for (const dayId of selectedScheduleItems) {
                try {
                    const result = await leagueResultService.get(`${leagueId}_${dayId}`);
                    if (result && result.members) {
                        Object.entries(result.members).forEach(([encodedEmail, member]) => {
                            if (!memberPayouts[encodedEmail]) {
                                const leagueMember = leagueModel.members[encodedEmail];
                                const flightName = member.flightId ? leagueModel.flights.find(f => f.id === member.flightId)?.name || member.flightId : 'Unknown';
                                memberPayouts[encodedEmail] = {
                                    firstName: leagueMember?.firstName || '',
                                    lastName: leagueMember?.lastName || '',
                                    totalLeaguePayout: 0,
                                    totalSkinPayout: 0,
                                    totalPayout: 0,
                                    flightName: flightName,
                                    payouts: [],
                                };
                            }

                            memberPayouts[encodedEmail].totalLeaguePayout += member.totalLeaguePayout || 0;
                            memberPayouts[encodedEmail].totalSkinPayout += (member.totalGrossSkinsPayout || 0) + (member.totalNetSkinsPayout || 0);
                            memberPayouts[encodedEmail].totalPayout += member.totalPayout || 0;

                            // Collect individual payouts
                            if (member.payouts && member.payouts.length > 0) {
                                member.payouts.forEach(payout => {
                                    if (payout.amount && payout.amount > 0) {
                                        memberPayouts[encodedEmail].payouts.push({
                                            description: payout.description || payout.code || 'Unknown',
                                            amount: payout.amount,
                                            dayId: dayId,
                                            code: payout.code || undefined,
                                            hole: payout.hole || undefined
                                        });
                                    }
                                });
                            }

                            // Collect skin payouts from the league result
                            if (result.grossSkinPrizes && result.grossSkinPrizes.length > 0) {
                                result.grossSkinPrizes.forEach(skinPrize => {
                                    if (skinPrize.encodedEmail === encodedEmail && skinPrize.amount && skinPrize.amount > 0) {
                                        memberPayouts[encodedEmail].payouts.push({
                                            description: `Gross Skin (Hole ${skinPrize.hole})`,
                                            amount: skinPrize.amount,
                                            dayId: dayId,
                                            code: 'GRSSKN',
                                            hole: skinPrize.hole
                                        });
                                    }
                                });
                            }

                            if (result.netSkinPrizes && result.netSkinPrizes.length > 0) {
                                result.netSkinPrizes.forEach(skinPrize => {
                                    if (skinPrize.encodedEmail === encodedEmail && skinPrize.amount && skinPrize.amount > 0) {
                                        memberPayouts[encodedEmail].payouts.push({
                                            description: `Net Skin (Hole ${skinPrize.hole})`,
                                            amount: skinPrize.amount,
                                            dayId: dayId,
                                            code: 'NETSKN',
                                            hole: skinPrize.hole
                                        });
                                    }
                                });
                            }
                        });
                    }
                } catch (err) {
                    console.warn(`Failed to fetch results for day ${dayId}:`, err);
                }
            }

            // Validate payout consistency for each member
            const validationErrors: string[] = [];
            Object.entries(memberPayouts).forEach(([encodedEmail, memberData]) => {
                const calculatedTotal = Math.round((memberData.totalLeaguePayout + memberData.totalSkinPayout) * 100) / 100;
                const actualTotal = Math.round(memberData.totalPayout * 100) / 100;

                if (calculatedTotal !== actualTotal) {
                    const memberName = getMemberName(encodedEmail);
                    validationErrors.push(
                        `${memberName} (${encodedEmail}): totalPayout (${actualTotal}) does not match calculated sum (${calculatedTotal}) = leaguePayout (${memberData.totalLeaguePayout}) + skinPayout (${memberData.totalSkinPayout})`
                    );
                }
            });

            if (validationErrors.length > 0) {
                const errorMessage = `Payout validation failed for ${validationErrors.length} member(s):\n\n${validationErrors.join('\n')}`;
                console.error(errorMessage);
                setError(errorMessage);
                return;
            }

            // Convert to array and sort by last name, then first name
            const sortedMembers = Object.entries(memberPayouts)
                .map(([encodedEmail, data]) => ({
                    encodedEmail,
                    ...data
                }))
                .sort((a, b) => {
                    const lastNameCompare = a.lastName.localeCompare(b.lastName);
                    if (lastNameCompare !== 0) return lastNameCompare;
                    return a.firstName.localeCompare(b.firstName);
                });

            // Generate CSV content
            const csvHeaders = ['Last Name', 'First Name', 'Total Payout', 'Total League Payout', 'Total Skin Payout', 'Flight Name', 'Payout Breakdown'];
            const csvRows = sortedMembers.map(member => {
                // Create breakdown text from actual payouts
                let breakdownText = '';
                if (member.payouts.length > 0) {
                    // Process each payout individually using code and properties
                    const breakdownParts = member.payouts.map(payout => {
                        const code = payout.code;
                        const amount = payout.amount;
                        const hole = payout.hole;

                        // Handle hole-specific payouts using codes
                        if (hole && code && ['NETBRD', 'GRSBRD', 'GRSEGL', 'GRSALB', 'GRSACE'].includes(code)) {
                            let description = '';
                            switch (code) {
                                case 'NETBRD':
                                    description = 'Net Birdie';
                                    break;
                                case 'GRSBRD':
                                    description = 'Birdie';
                                    break;
                                case 'GRSEGL':
                                    description = 'Eagle';
                                    break;
                                case 'GRSALB':
                                    description = 'Albatross';
                                    break;
                                case 'GRSACE':
                                    description = 'Ace';
                                    break;
                                default:
                                    description = payout.description;
                            }
                            return `${description} (Hole ${hole}): $${amount.toFixed(2)}`;
                        } else {
                            // For non-hole-specific payouts, use the description
                            return `${payout.description}: $${amount.toFixed(2)}`;
                        }
                    });
                    breakdownText = breakdownParts.join(', ');
                } else {
                    breakdownText = 'No payouts';
                }

                return [
                    member.lastName,
                    member.firstName,
                    member.totalPayout.toFixed(2),
                    member.totalLeaguePayout.toFixed(2),
                    member.totalSkinPayout.toFixed(2),
                    member.flightName,
                    breakdownText
                ];
            });

            // Calculate grand totals
            const grandTotalPayout = sortedMembers.reduce((sum, member) => sum + member.totalPayout, 0);
            const grandTotalLeaguePayout = sortedMembers.reduce((sum, member) => sum + member.totalLeaguePayout, 0);
            const grandTotalSkinPayout = sortedMembers.reduce((sum, member) => sum + member.totalSkinPayout, 0);

            // Add grand total row
            const grandTotalRow = [
                'GRAND TOTAL',
                '',
                grandTotalPayout.toFixed(2),
                grandTotalLeaguePayout.toFixed(2),
                grandTotalSkinPayout.toFixed(2),
                '',
                `League: $${grandTotalLeaguePayout.toFixed(2)}, Skins: $${grandTotalSkinPayout.toFixed(2)}`
            ];

            const csvContent = [csvHeaders, ...csvRows, grandTotalRow]
                .map(row => row.map(cell => `"${cell}"`).join(','))
                .join('\n');

            // Create and download CSV file
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);

            // Generate filename with leagueId and last 5 chars of each dayId
            const dayIdSuffixes = Array.from(selectedScheduleItems)
                .sort()
                .map(dayId => dayId.slice(-5).replace('_', '-'))
                .join('_');
            const filename = `${leagueId}_members_${dayIdSuffixes}.csv`;

            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

        } catch (err: any) {
            console.error('Error generating payouts CSV:', err);
            setError('Failed to generate payouts CSV: ' + err.message);
        } finally {
            setGeneratingPayouts(false);
        }
    };

    const generateFlightsCSV = async () => {
        if (!leagueModel || selectedScheduleItems.size === 0) return;

        setGeneratingFlights(true);
        try {
            const leagueResultService = resolveType(LeagueResultService);

            // Create flight ID to name mapping and valid flight IDs set
            const flightIdToName: Record<string, string> = {};
            const validFlightIds = new Set<string>();
            leagueModel.flights.forEach(flight => {
                flightIdToName[flight.id] = flight.name;
                validFlightIds.add(flight.id);
            });

            // Collect flight data
            const flightData: Record<string, {
                flightId: string;
                flightName: string;
                memberCount: number;
                totalLeaguePayout: number;
                totalSkinPayout: number;
                totalGrossSkinPayout: number;
                totalNetSkinPayout: number;
                totalPayout: number;
                grossRankPayout: number;
                netRankPayout: number;
                eaglePayout: number;
                birdiePayout: number;
                netBirdiePayout: number;
                pinPrizePayout: number;
                members: Array<{
                    encodedEmail: string;
                    firstName: string;
                    lastName: string;
                    netScore: number;
                    grossScore: number;
                }>;
            }> = {};

            // Process each selected schedule item
            for (const dayId of selectedScheduleItems) {
                try {
                    const result = await leagueResultService.get(`${leagueId}_${dayId}`);
                    if (result && result.members) {
                        Object.entries(result.members).forEach(([encodedEmail, member]) => {
                            const flightId = member.flightId || 'Unknown';

                            // Skip members with invalid flight IDs
                            if (!validFlightIds.has(flightId)) {
                                console.warn(`Skipping member ${encodedEmail} with invalid flightId: ${flightId}`);
                                return;
                            }

                            if (!flightData[flightId]) {
                                flightData[flightId] = {
                                    flightId,
                                    flightName: flightIdToName[flightId] || flightId,
                                    memberCount: 0,
                                    totalLeaguePayout: 0,
                                    totalSkinPayout: 0,
                                    totalGrossSkinPayout: 0,
                                    totalNetSkinPayout: 0,
                                    totalPayout: 0,
                                    grossRankPayout: 0,
                                    netRankPayout: 0,
                                    eaglePayout: 0,
                                    birdiePayout: 0,
                                    netBirdiePayout: 0,
                                    pinPrizePayout: 0,
                                    members: [],
                                };
                            }

                            flightData[flightId].totalLeaguePayout += member.totalLeaguePayout || 0;
                            flightData[flightId].totalGrossSkinPayout += member.totalGrossSkinsPayout || 0;
                            flightData[flightId].totalNetSkinPayout += member.totalNetSkinsPayout || 0;
                            flightData[flightId].totalSkinPayout += (member.totalGrossSkinsPayout || 0) + (member.totalNetSkinsPayout || 0);
                            flightData[flightId].totalPayout += member.totalPayout || 0;

                            // Collect specific payout types
                            if (member.payouts && member.payouts.length > 0) {
                                member.payouts.forEach(payout => {
                                    if (payout.amount && payout.amount > 0) {
                                        const code = payout.code;
                                        if (code === 'GRSEGL') {
                                            flightData[flightId].eaglePayout += payout.amount;
                                        } else if (code === 'GRSBRD') {
                                            flightData[flightId].birdiePayout += payout.amount;
                                        } else if (code === 'NETBRD') {
                                            flightData[flightId].netBirdiePayout += payout.amount;
                                        } else if (code && ['GRSRNK1ST', 'GRSRNK2ND', 'GRSRNK3RD'].includes(code)) {
                                            flightData[flightId].grossRankPayout += payout.amount;
                                        } else if (code && ['NETRNK1ST', 'NETRNK2ND', 'NETRNK3RD'].includes(code)) {
                                            flightData[flightId].netRankPayout += payout.amount;
                                        } else if (code && ['CTPTEESHT', 'CTP2NDSHT', 'CTP3RDSHT', 'CTP3RDSHTOFFGRN', 'LNGPUT'].includes(code)) {
                                            flightData[flightId].pinPrizePayout += payout.amount;
                                        }
                                    }
                                });
                            }

                            // Add member if not already present
                            const existingMember = flightData[flightId].members.find(m => m.encodedEmail === encodedEmail);
                            if (!existingMember) {
                                const leagueMember = leagueModel.members[encodedEmail];
                                flightData[flightId].members.push({
                                    encodedEmail,
                                    firstName: leagueMember?.firstName || '',
                                    lastName: leagueMember?.lastName || '',
                                    netScore: member.netScore || 0,
                                    grossScore: member.grossScore || 0,
                                });
                                flightData[flightId].memberCount++;
                            }
                        });
                    }
                } catch (err) {
                    console.warn(`Failed to fetch results for day ${dayId}:`, err);
                }
            }

            // Validate payout consistency for each flight
            const validationErrors: string[] = [];
            Object.entries(flightData).forEach(([_, flightData]) => {
                const calculatedTotal = Math.round((flightData.totalLeaguePayout + flightData.totalSkinPayout) * 100) / 100;
                const actualTotal = Math.round(flightData.totalPayout * 100) / 100;

                if (calculatedTotal !== actualTotal) {
                    validationErrors.push(
                        `Flight ${flightData.flightName}: totalPayout (${actualTotal}) does not match calculated sum (${calculatedTotal}) = leaguePayout (${flightData.totalLeaguePayout}) + skinPayout (${flightData.totalSkinPayout})`
                    );
                }
            });

            if (validationErrors.length > 0) {
                const errorMessage = `Flight payout validation failed for ${validationErrors.length} flight(s):\n\n${validationErrors.join('\n')}`;
                console.error(errorMessage);
                setError(errorMessage);
                return;
            }

            // Convert to array and sort by flight name
            const sortedFlights = Object.values(flightData)
                .sort((a, b) => a.flightName.localeCompare(b.flightName));

            // Generate CSV content
            const csvHeaders = ['Flight Name', 'Gross Rank Payout', 'Net Rank Payout', 'Eagle Payout', 'Birdie Payout', 'Net Birdie Payout', 'Pin Prize Payout', 'Total League Payout', 'Total Gross Skin Payout', 'Total Net Skin Payout', 'Total Skin Payout', 'Total Payout'];
            const csvRows = sortedFlights.map(flight => [
                flight.flightName,
                flight.grossRankPayout.toFixed(2),
                flight.netRankPayout.toFixed(2),
                flight.eaglePayout.toFixed(2),
                flight.birdiePayout.toFixed(2),
                flight.netBirdiePayout.toFixed(2),
                flight.pinPrizePayout.toFixed(2),
                flight.totalLeaguePayout.toFixed(2),
                flight.totalGrossSkinPayout.toFixed(2),
                flight.totalNetSkinPayout.toFixed(2),
                flight.totalSkinPayout.toFixed(2),
                flight.totalPayout.toFixed(2)
            ]);

            // Calculate grand totals
            const grandTotalLeaguePayout = sortedFlights.reduce((sum, flight) => sum + flight.totalLeaguePayout, 0);
            const grandTotalGrossSkinPayout = sortedFlights.reduce((sum, flight) => sum + flight.totalGrossSkinPayout, 0);
            const grandTotalNetSkinPayout = sortedFlights.reduce((sum, flight) => sum + flight.totalNetSkinPayout, 0);
            const grandTotalSkinPayout = sortedFlights.reduce((sum, flight) => sum + flight.totalSkinPayout, 0);
            const grandTotalGrossRankPayout = sortedFlights.reduce((sum, flight) => sum + flight.grossRankPayout, 0);
            const grandTotalNetRankPayout = sortedFlights.reduce((sum, flight) => sum + flight.netRankPayout, 0);
            const grandTotalEaglePayout = sortedFlights.reduce((sum, flight) => sum + flight.eaglePayout, 0);
            const grandTotalBirdiePayout = sortedFlights.reduce((sum, flight) => sum + flight.birdiePayout, 0);
            const grandTotalNetBirdiePayout = sortedFlights.reduce((sum, flight) => sum + flight.netBirdiePayout, 0);
            const grandTotalPinPrizePayout = sortedFlights.reduce((sum, flight) => sum + flight.pinPrizePayout, 0);
            const grandTotalPayout = sortedFlights.reduce((sum, flight) => sum + flight.totalPayout, 0);

            // Add grand total row
            const grandTotalRow = [
                'GRAND TOTAL',
                grandTotalGrossRankPayout.toFixed(2),
                grandTotalNetRankPayout.toFixed(2),
                grandTotalEaglePayout.toFixed(2),
                grandTotalBirdiePayout.toFixed(2),
                grandTotalNetBirdiePayout.toFixed(2),
                grandTotalPinPrizePayout.toFixed(2),
                grandTotalLeaguePayout.toFixed(2),
                grandTotalGrossSkinPayout.toFixed(2),
                grandTotalNetSkinPayout.toFixed(2),
                grandTotalSkinPayout.toFixed(2),
                grandTotalPayout.toFixed(2)
            ];

            const csvContent = [csvHeaders, ...csvRows, grandTotalRow]
                .map(row => row.map(cell => `"${cell}"`).join(','))
                .join('\n');

            // Create and download CSV file
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);

            // Generate filename with leagueId and last 5 chars of each dayId
            const dayIdSuffixes = Array.from(selectedScheduleItems)
                .sort()
                .map(dayId => dayId.slice(-5).replace('_', '-'))
                .join('_');
            const filename = `${leagueId}_flights_${dayIdSuffixes}.csv`;

            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

        } catch (err: any) {
            console.error('Error generating flights CSV:', err);
            setError('Failed to generate flights CSV: ' + err.message);
        } finally {
            setGeneratingFlights(false);
        }
    };

    const generateTeesCSV = async () => {
        if (!leagueModel || selectedScheduleItems.size === 0) return;

        setGeneratingTees(true);
        try {
            const leagueResultService = resolveType(LeagueResultService);

            // Create tee name mapping from flights
            const flightIdToTeeName: Record<string, string> = {};
            leagueModel.flights.forEach(flight => {
                flightIdToTeeName[flight.id] = flight.teeName;
            });

            // Collect tee data
            const teeData: Record<string, {
                teeName: string;
                memberCount: number;
                totalLeaguePayout: number;
                totalSkinPayout: number;
                totalGrossSkinPayout: number;
                totalNetSkinPayout: number;
                totalPayout: number;
                grossRankPayout: number;
                netRankPayout: number;
                eaglePayout: number;
                birdiePayout: number;
                netBirdiePayout: number;
                pinPrizePayout: number;
                members: Array<{
                    encodedEmail: string;
                    firstName: string;
                    lastName: string;
                    netScore: number;
                    grossScore: number;
                }>;
            }> = {};

            // Process each selected schedule item
            for (const dayId of selectedScheduleItems) {
                try {
                    const result = await leagueResultService.get(`${leagueId}_${dayId}`);
                    if (result && result.members) {
                        Object.entries(result.members).forEach(([encodedEmail, member]) => {
                            const flightId = member.flightId || 'Unknown';
                            const teeName = flightIdToTeeName[flightId] || 'Unknown';

                            // Skip members with invalid flight IDs (which would result in unknown tee names)
                            if (!flightIdToTeeName[flightId]) {
                                console.warn(`Skipping member ${encodedEmail} with invalid flightId: ${flightId}`);
                                return;
                            }

                            if (!teeData[teeName]) {
                                teeData[teeName] = {
                                    teeName,
                                    memberCount: 0,
                                    totalLeaguePayout: 0,
                                    totalSkinPayout: 0,
                                    totalGrossSkinPayout: 0,
                                    totalNetSkinPayout: 0,
                                    totalPayout: 0,
                                    grossRankPayout: 0,
                                    netRankPayout: 0,
                                    eaglePayout: 0,
                                    birdiePayout: 0,
                                    netBirdiePayout: 0,
                                    pinPrizePayout: 0,
                                    members: [],
                                };
                            }

                            teeData[teeName].totalLeaguePayout += member.totalLeaguePayout || 0;
                            teeData[teeName].totalGrossSkinPayout += member.totalGrossSkinsPayout || 0;
                            teeData[teeName].totalNetSkinPayout += member.totalNetSkinsPayout || 0;
                            teeData[teeName].totalSkinPayout += (member.totalGrossSkinsPayout || 0) + (member.totalNetSkinsPayout || 0);
                            teeData[teeName].totalPayout += member.totalPayout || 0;

                            // Collect specific payout types
                            if (member.payouts && member.payouts.length > 0) {
                                member.payouts.forEach(payout => {
                                    if (payout.amount && payout.amount > 0) {
                                        const code = payout.code;
                                        if (code === 'GRSEGL') {
                                            teeData[teeName].eaglePayout += payout.amount;
                                        } else if (code === 'GRSBRD') {
                                            teeData[teeName].birdiePayout += payout.amount;
                                        } else if (code === 'NETBRD') {
                                            teeData[teeName].netBirdiePayout += payout.amount;
                                        } else if (code && ['GRSRNK1ST', 'GRSRNK2ND', 'GRSRNK3RD'].includes(code)) {
                                            teeData[teeName].grossRankPayout += payout.amount;
                                        } else if (code && ['NETRNK1ST', 'NETRNK2ND', 'NETRNK3RD'].includes(code)) {
                                            teeData[teeName].netRankPayout += payout.amount;
                                        } else if (code && ['CTPTEESHT', 'CTP2NDSHT', 'CTP3RDSHT', 'CTP3RDSHTOFFGRN', 'LNGPUT'].includes(code)) {
                                            teeData[teeName].pinPrizePayout += payout.amount;
                                        }
                                    }
                                });
                            }

                            // Add member if not already present
                            const existingMember = teeData[teeName].members.find(m => m.encodedEmail === encodedEmail);
                            if (!existingMember) {
                                const leagueMember = leagueModel.members[encodedEmail];
                                teeData[teeName].members.push({
                                    encodedEmail,
                                    firstName: leagueMember?.firstName || '',
                                    lastName: leagueMember?.lastName || '',
                                    netScore: member.netScore || 0,
                                    grossScore: member.grossScore || 0,
                                });
                                teeData[teeName].memberCount++;
                            }
                        });
                    }
                } catch (err) {
                    console.warn(`Failed to fetch results for day ${dayId}:`, err);
                }
            }

            // Validate payout consistency for each tee
            const validationErrors: string[] = [];
            Object.entries(teeData).forEach(([_, teeData]) => {
                const calculatedTotal = Math.round((teeData.totalLeaguePayout + teeData.totalSkinPayout) * 100) / 100;
                const actualTotal = Math.round(teeData.totalPayout * 100) / 100;

                if (calculatedTotal !== actualTotal) {
                    validationErrors.push(
                        `Tee ${teeData.teeName}: totalPayout (${actualTotal}) does not match calculated sum (${calculatedTotal}) = leaguePayout (${teeData.totalLeaguePayout}) + skinPayout (${teeData.totalSkinPayout})`
                    );
                }
            });

            if (validationErrors.length > 0) {
                const errorMessage = `Tee payout validation failed for ${validationErrors.length} tee(s):\n\n${validationErrors.join('\n')}`;
                console.error(errorMessage);
                setError(errorMessage);
                return;
            }

            // Convert to array and sort by tee name
            const sortedTees = Object.values(teeData)
                .sort((a, b) => a.teeName.localeCompare(b.teeName));

            // Generate CSV content
            const csvHeaders = ['Tee Name', 'Gross Rank Payout', 'Net Rank Payout', 'Eagle Payout', 'Birdie Payout', 'Net Birdie Payout', 'Pin Prize Payout', 'Total League Payout', 'Total Gross Skin Payout', 'Total Net Skin Payout', 'Total Skin Payout', 'Total Payout'];
            const csvRows = sortedTees.map(tee => [
                tee.teeName,
                tee.grossRankPayout.toFixed(2),
                tee.netRankPayout.toFixed(2),
                tee.eaglePayout.toFixed(2),
                tee.birdiePayout.toFixed(2),
                tee.netBirdiePayout.toFixed(2),
                tee.pinPrizePayout.toFixed(2),
                tee.totalLeaguePayout.toFixed(2),
                tee.totalGrossSkinPayout.toFixed(2),
                tee.totalNetSkinPayout.toFixed(2),
                tee.totalSkinPayout.toFixed(2),
                tee.totalPayout.toFixed(2)
            ]);

            // Calculate grand totals
            const grandTotalLeaguePayout = sortedTees.reduce((sum, tee) => sum + tee.totalLeaguePayout, 0);
            const grandTotalGrossSkinPayout = sortedTees.reduce((sum, tee) => sum + tee.totalGrossSkinPayout, 0);
            const grandTotalNetSkinPayout = sortedTees.reduce((sum, tee) => sum + tee.totalNetSkinPayout, 0);
            const grandTotalSkinPayout = sortedTees.reduce((sum, tee) => sum + tee.totalSkinPayout, 0);
            const grandTotalGrossRankPayout = sortedTees.reduce((sum, tee) => sum + tee.grossRankPayout, 0);
            const grandTotalNetRankPayout = sortedTees.reduce((sum, tee) => sum + tee.netRankPayout, 0);
            const grandTotalEaglePayout = sortedTees.reduce((sum, tee) => sum + tee.eaglePayout, 0);
            const grandTotalBirdiePayout = sortedTees.reduce((sum, tee) => sum + tee.birdiePayout, 0);
            const grandTotalNetBirdiePayout = sortedTees.reduce((sum, tee) => sum + tee.netBirdiePayout, 0);
            const grandTotalPinPrizePayout = sortedTees.reduce((sum, tee) => sum + tee.pinPrizePayout, 0);
            const grandTotalPayout = sortedTees.reduce((sum, tee) => sum + tee.totalPayout, 0);

            // Add grand total row
            const grandTotalRow = [
                'GRAND TOTAL',
                grandTotalGrossRankPayout.toFixed(2),
                grandTotalNetRankPayout.toFixed(2),
                grandTotalEaglePayout.toFixed(2),
                grandTotalBirdiePayout.toFixed(2),
                grandTotalNetBirdiePayout.toFixed(2),
                grandTotalPinPrizePayout.toFixed(2),
                grandTotalLeaguePayout.toFixed(2),
                grandTotalGrossSkinPayout.toFixed(2),
                grandTotalNetSkinPayout.toFixed(2),
                grandTotalSkinPayout.toFixed(2),
                grandTotalPayout.toFixed(2)
            ];

            const csvContent = [csvHeaders, ...csvRows, grandTotalRow]
                .map(row => row.map(cell => `"${cell}"`).join(','))
                .join('\n');

            // Create and download CSV file
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);

            // Generate filename with leagueId and last 5 chars of each dayId
            const dayIdSuffixes = Array.from(selectedScheduleItems)
                .sort()
                .map(dayId => dayId.slice(-5).replace('_', '-'))
                .join('_');
            const filename = `${leagueId}_tees_${dayIdSuffixes}.csv`;

            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

        } catch (err: any) {
            console.error('Error generating tees CSV:', err);
            setError('Failed to generate tees CSV: ' + err.message);
        } finally {
            setGeneratingTees(false);
        }
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    if (error) {
        return (
            <div style={{ padding: theme.spacing.LARGE }}>
                <div style={{
                    color: theme.colors.ERROR,
                    textAlign: 'center',
                    padding: theme.spacing.LARGE
                }}>
                    {error}
                </div>
            </div>
        );
    }

    if (!leagueModel) {
        return (
            <div style={{ padding: theme.spacing.LARGE }}>
                <div style={{
                    color: theme.colors.TEXT_SECONDARY,
                    textAlign: 'center',
                    padding: theme.spacing.LARGE
                }}>
                    League not found
                </div>
            </div>
        );
    }

    return (
        <div style={{ padding: theme.spacing.LARGE }}>
            <div style={{
                marginBottom: theme.spacing.LARGE,
            }}>
                <h1 style={{
                    color: theme.colors.TEXT,
                    marginBottom: theme.spacing.SMALL,
                    fontSize: '1.8em'
                }}>
                    League Reports
                </h1>
                <h2 style={{
                    color: theme.colors.TEXT_SECONDARY,
                    marginBottom: theme.spacing.SMALL,
                    fontSize: '1.2em'
                }}>
                    {leagueModel.name}
                </h2>
            </div>

            {/* Schedule Items Selection */}
            <div style={{
                background: theme.colors.SURFACE,
                border: `1px solid ${theme.colors.BORDER}`,
                borderRadius: theme.borderRadius.MEDIUM,
                padding: theme.spacing.LARGE,
                marginBottom: theme.spacing.LARGE,
            }}>
                <div style={{
                    marginBottom: theme.spacing.MEDIUM,
                }}>
                    <h3 style={{
                        color: theme.colors.TEXT,
                        fontSize: '1.3em',
                        margin: 0,
                    }}>
                        Select Report Sources
                    </h3>
                </div>

                <div style={{
                    display: 'grid',
                    gap: theme.spacing.SMALL,
                }}>
                    {leagueModel.schedule
                        .filter(schedule => schedule.status === 'completed' || schedule.status === 'cancelled')
                        .map((schedule, index) => {
                            const isCompleted = schedule.status === 'completed';
                            const isCancelled = schedule.status === 'cancelled';

                            return (
                                <div key={schedule.dayId} style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: theme.spacing.MEDIUM,
                                    background: isCancelled ? theme.colors.BORDER : theme.colors.BACKGROUND,
                                    borderRadius: theme.borderRadius.SMALL,
                                    border: selectedScheduleItems.has(schedule.dayId)
                                        ? `2px solid ${theme.colors.PRIMARY}`
                                        : `1px solid ${theme.colors.BORDER}`,
                                    opacity: isCancelled ? 0.7 : 1,
                                }}>
                                    {isCompleted && (
                                        <div
                                            onClick={() => handleScheduleItemToggle(schedule.dayId)}
                                            style={{
                                                marginRight: theme.spacing.MEDIUM,
                                                width: '24px',
                                                height: '24px',
                                                borderRadius: '4px',
                                                border: `2px solid ${selectedScheduleItems.has(schedule.dayId) ? theme.colors.SUCCESS : theme.colors.BORDER}`,
                                                background: selectedScheduleItems.has(schedule.dayId) ? theme.colors.SUCCESS : 'transparent',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                            }}
                                        >
                                            {selectedScheduleItems.has(schedule.dayId) && (
                                                <svg
                                                    width="16"
                                                    height="16"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"
                                                        fill="white"
                                                    />
                                                </svg>
                                            )}
                                        </div>
                                    )}
                                    {isCancelled && (
                                        <div style={{
                                            marginRight: theme.spacing.MEDIUM,
                                            width: '24px',
                                            height: '24px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}>
                                            <svg
                                                width="16"
                                                height="16"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
                                                    fill={theme.colors.TEXT_SECONDARY}
                                                />
                                            </svg>
                                        </div>
                                    )}
                                    <div style={{ flex: 1 }}>
                                        <div style={{
                                            fontWeight: 'bold',
                                            color: isCancelled ? theme.colors.TEXT_SECONDARY : theme.colors.TEXT,
                                            fontSize: '1.1em',
                                        }}>
                                            Week {index + 1} - {formatDate(schedule.dayId)}
                                        </div>
                                        {schedule.skinsDayId1 && (
                                            <div style={{
                                                color: theme.colors.ACCENT,
                                                fontSize: '0.9em',
                                                marginTop: '2px',
                                            }}>
                                                (Skins Payout)
                                            </div>
                                        )}
                                        <div style={{
                                            color: isCancelled ? theme.colors.TEXT_SECONDARY : theme.colors.TEXT_SECONDARY,
                                            fontSize: '0.9em',
                                        }}>
                                            Status: {schedule.status}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                </div>


            </div>

            {/* Generate Buttons */}
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: theme.spacing.MEDIUM,
                marginTop: theme.spacing.LARGE,
            }}>
                <button
                    onClick={generatePayoutsCSV}
                    disabled={selectedScheduleItems.size === 0 || generatingPayouts || generatingFlights || generatingTees}
                    style={{
                        background: (selectedScheduleItems.size === 0 || generatingPayouts) ? theme.colors.BORDER : theme.colors.SUCCESS,
                        color: theme.colors.WHITE,
                        border: 'none',
                        padding: '16px 32px',
                        borderRadius: '8px',
                        cursor: (selectedScheduleItems.size === 0 || generatingPayouts || generatingFlights || generatingTees) ? 'default' : 'pointer',
                        fontSize: '1.1em',
                        fontWeight: 'bold',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    }}
                >
                    {generatingPayouts ? 'Generating...' : 'Payouts By Member'}
                </button>
                <button
                    onClick={generateFlightsCSV}
                    disabled={selectedScheduleItems.size === 0 || generatingPayouts || generatingFlights || generatingTees}
                    style={{
                        background: (selectedScheduleItems.size === 0 || generatingFlights) ? theme.colors.BORDER : theme.colors.PRIMARY,
                        color: theme.colors.WHITE,
                        border: 'none',
                        padding: '16px 32px',
                        borderRadius: '8px',
                        cursor: (selectedScheduleItems.size === 0 || generatingPayouts || generatingFlights || generatingTees) ? 'default' : 'pointer',
                        fontSize: '1.1em',
                        fontWeight: 'bold',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    }}
                >
                    {generatingFlights ? 'Generating...' : 'Payouts By Flight'}
                </button>
                <button
                    onClick={generateTeesCSV}
                    disabled={selectedScheduleItems.size === 0 || generatingPayouts || generatingFlights || generatingTees}
                    style={{
                        background: (selectedScheduleItems.size === 0 || generatingTees) ? theme.colors.BORDER : theme.colors.ACCENT,
                        color: theme.colors.WHITE,
                        border: 'none',
                        padding: '16px 32px',
                        borderRadius: '8px',
                        cursor: (selectedScheduleItems.size === 0 || generatingPayouts || generatingFlights || generatingTees) ? 'default' : 'pointer',
                        fontSize: '1.1em',
                        fontWeight: 'bold',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    }}
                >
                    {generatingTees ? 'Generating...' : 'Payouts By Tee'}
                </button>
            </div>
        </div>
    );
};

export default LeagueReports; 